import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { Routes, Route } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import AppNotification from "./AppNotification";
import Authorization from "./authentication/Authorization";
import { selectUser } from "./@slices/userSlice";
import { useSelector } from "react-redux";
import withAppProviders from "./provider/withProvider";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import { AppProvider } from "./provider/AppProvider";
import { AuthProvider } from "./authentication/AuthContext";
import { BrowserRouter } from "./provider/BrowserRouter";
import { SplashScreen } from "@components";
import { MuiTheme, SystemConfig } from "./@setting";
import CssBaseline from "@mui/material/CssBaseline";

// Lazy load pages for better performance
const LocationPage = lazy(() => import("./pages/locationPage"));
const ReportPage = lazy(() => import("./pages/reportPage"));
const SettingPage = lazy(() => import("./pages/settingPage"));
const SummaryPage = lazy(() => import("./pages/summaryPage"));
const TruckDetailPage = lazy(() => import("./pages/truckDetailPage"));
const SignInSide = lazy(() => import("./app/account/sign-in/SignInSide"));
const SignOutPage = lazy(() => import("./app/account/sign-out/SignOutPage"));
const SignUpPage = lazy(() => import("./app/account/sign-up/SignUpPage"));
const ForgotPasswordPage = lazy(() => import("./app/account/forgot-password/ForgotPasswordPage"));
const PageNotFound = lazy(() => import("./pages/pageNotFound"));

const googleMapsLibraries = ["drawing"];

const App = () => {
	const user = useSelector(selectUser);
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
		libraries: googleMapsLibraries,
	});

	useEffect(() => {
		document.title = `${SystemConfig.AppName}`;
	}, []);

	return (
		<CssVarsProvider theme={MuiTheme}>
			<CssBaseline />
			<BrowserRouter>
				<AuthProvider>
					<AppProvider>
						<Authorization
							userRole={user?.role}
							loginRedirectUrl={"/location"}>
							<AppNotification />
							<Suspense fallback={<SplashScreen />}>
								<Routes>
									<Route path="/" element={<LocationPage />} />
									<Route path="/location" element={<LocationPage />} />
									<Route path="/sign-in" element={<SignInSide />} />
									<Route path="/sign-out" element={<SignOutPage />} />
									<Route path="/sign-up" element={<SignUpPage />} />
									<Route path="/forgot-password" element={<ForgotPasswordPage />} />
									<Route path="/report" element={<ReportPage />} />
									<Route path="/summary" element={<SummaryPage />} />
									<Route path="/truck-detail/:id" element={<TruckDetailPage />} />
									<Route path="/setting" element={<SettingPage />} />
									<Route path="*" element={<PageNotFound />} />
									<Route path="/loading" element={<SplashScreen />} />
								</Routes>
							</Suspense>
						</Authorization>
					</AppProvider>
				</AuthProvider>
			</BrowserRouter>
		</CssVarsProvider>
	);
};

export default withAppProviders(App)();
